import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supervisorApi } from '@/features/workspace/api';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api/client';
import '@/features/student/sv121.css';

type StudentRow = {
  id: string;
  academia_id: string;
  full_name: string;
  education_level: string;
  program: string;
  topic: string | null;
  progress_pct: number;
  course_year?: number | null;
  rating_score?: number | null;
  review_status: string;
  plan_status: string;
  defence_status: string;
};

type TopicVariant = { id: string; title: string; status: string; rationale?: string; result?: string };
type TopicQuestion = { id: string; q: string; a?: string };
type WorkMessage = { id: string; text: string; author?: string; at?: string };
type OrderFile = { id?: string; title: string; kind?: string; status?: string };
type LevelDocument = {
  id: string;
  title: string;
  file: string;
  type: string;
  version: number;
  canonical: boolean;
  file_url?: string;
};

type Detail = {
  draft_revision: number;
  has_unpublished_draft: boolean;
  student: StudentRow;
  overview: { next_actions: string[] };
  topic: {
    title: string;
    status: string;
    variants: TopicVariant[];
    questions?: TopicQuestion[];
    translations?: { kz?: string; ru?: string; en?: string };
  };
  plan: {
    status: string;
    version: string;
    intro?: { actuality?: string; goal?: string; tasks?: string[]; tasks_text?: string };
    conclusion?: string;
    bibliography?: string;
    sections: Array<{ num?: string; title: string; status: string; subs?: string[] }>;
    tasks: Array<{ id: string; title: string; deadline: string; status: string; desc?: string; result?: string }>;
    versions?: Array<{ at: string; actor: string; label: string; note: string }>;
  };
  literature: Array<{ id: string; title: string; added_by?: string; status: string; note?: string }>;
  thesis: Array<{ id: string; title: string; status: string; version: string }>;
  documents: Array<{ id: string; title: string; kind: string; size: string; status: string }>;
  level_documents?: LevelDocument[];
  nir: Array<{ id: string; title: string; status: string; kind: string; file_url?: string | null; evidence_url?: string | null; note?: string | null }>;
  deadlines: Array<{ id: string; title: string; date: string; status: string }>;
  consultations: Array<{ id: string; title: string; date: string; status: string }>;
  messages?: WorkMessage[];
  order?: OrderFile | null;
  consultant?: { name?: string; organization?: string; degree?: string };
  writing_checks?: Record<string, boolean>;
  defense: { status: string; stages: Array<{ id: string; title: string; status: string }> };
  last_ai?: { answer: string; suggestions: string[] } | null;
};

const LEVELS = [
  { id: 'BACHELOR', short: 'Бакалавриат', label: 'Дипломная работа', duration: '1 учебный год · 4 курс', accent: '#41d2ff', programs: 2, nir: '1 Исследовательский результат', hint: 'Для 6B01717 добавлять методическую/педагогическую составляющую; для 6B02305 — филологическую исследовательскую логику.' },
  { id: 'MASTER', short: 'Магистратура', label: 'Магистерская диссертация', duration: '2 года · 4 семестра', accent: '#9e80ff', programs: 2, nir: '4 НИРМ', hint: 'План связывает тему, главы, НИРМ и публикационный результат.' },
  { id: 'DOCTORAL', short: 'Докторантура', label: 'Докторская диссертация', duration: '3 года · 6 семестров', accent: '#49ddb7', programs: 2, nir: '6 НИРД / ДҒЗЖ', hint: 'Тема, отечественный руководитель, зарубежный консультант и НИРД идут одним контуром.' },
] as const;

const NAV = [
  { id: 'overview', label: 'Шолу', icon: 'dashboard', hint: 'Управление и контроль' },
  { id: 'students', label: 'Обучающиеся', icon: 'users', hint: 'Управление и контроль' },
  { id: 'topic', label: 'Темы', icon: 'topic', hint: 'Управление и контроль' },
  { id: 'plan', label: 'Планы и задачи', icon: 'plan', hint: 'Управление и контроль' },
  { id: 'resources', label: 'Документы', icon: 'files', hint: 'Управление и контроль' },
  { id: 'research', label: 'Публикации / НИР', icon: 'research', hint: 'Управление и контроль' },
  { id: 'timeline', label: 'Сроки / практика', icon: 'calendar', hint: 'Управление и контроль' },
  { id: 'meetings', label: 'Консультации', icon: 'chat', hint: 'Управление и контроль' },
  { id: 'defense', label: 'Предзащита / защита', icon: 'shield', hint: 'Управление и контроль' },
  { id: 'ai', label: 'Super AI', icon: 'spark', hint: 'Управление и контроль' },
  { id: 'literature', label: 'Литература', icon: 'files', hint: 'Согласование источников' },
  { id: 'writing', label: 'Основная работа', icon: 'plan', hint: 'Структура и проверка' },
] as const;

const NAV_COLORS = ['#41d2ff', '#49ddb7', '#e8b33b', '#9e80ff', '#66b7e8', '#49ddb7', '#e8b33b', '#ff7663', '#9e80ff', '#c7a7ff'];
const TOPIC_COLORS = ['#41d2ff', '#49ddb7', '#e8b33b', '#9e80ff', '#ff7663'];

const STUDENT_TABS = [
  ['card', 'Сводка'],
  ['topic', 'Тема'],
  ['plan', 'План'],
  ['tasks', 'Задачи'],
  ['resources', 'Документы'],
  ['research', 'НИР и публикации'],
  ['timeline', 'Сроки / практика'],
  ['meetings', 'Консультации'],
  ['defense', 'Защита'],
  ['literature', 'Литература'],
  ['writing', 'Основная работа'],
] as const;

type ViewId = (typeof NAV)[number]['id'] | 'card' | 'tasks';

type ComposerKind = 'documents' | 'order' | 'topics' | 'questions' | 'sections' | 'tasks' | 'literature' | 'messages' | 'consultations' | 'publications';

const COMPOSERS: Record<ComposerKind, { heading: string; fields: Array<{ name: string; label: string; multiline?: boolean; placeholder?: string; full?: boolean }> }> = {
  documents: {
    heading: 'Добавить общий материал уровня',
    fields: [
      { name: 'title', label: 'Наименование', full: true },
      { name: 'kind', label: 'Тип файла', placeholder: 'PDF / DOCX' },
    ],
  },
  order: { heading: 'Приказ', fields: [{ name: 'title', label: 'Название приказа' }] },
  topics: {
    heading: 'Новая тема',
    fields: [
      { name: 'title', label: 'Формулировка темы' },
      { name: 'note', label: 'Обоснование', multiline: true },
    ],
  },
  questions: { heading: 'Вопрос обучающемуся', fields: [{ name: 'title', label: 'Вопрос', multiline: true }] },
  sections: { heading: 'Новый раздел плана', fields: [{ name: 'title', label: 'Название раздела' }] },
  tasks: {
    heading: 'Новая задача',
    fields: [
      { name: 'title', label: 'Название' },
      { name: 'deadline', label: 'Срок', placeholder: '2026-10-20' },
      { name: 'note', label: 'Описание', multiline: true },
    ],
  },
  literature: {
    heading: 'Новый источник',
    fields: [
      { name: 'title', label: 'Название источника' },
      { name: 'note', label: 'Комментарий', multiline: true },
    ],
  },
  messages: { heading: 'Сообщение', fields: [{ name: 'title', label: 'Текст', multiline: true }] },
  consultations: {
    heading: 'Назначить консультацию',
    fields: [
      { name: 'title', label: 'Тема встречи' },
      { name: 'date', label: 'Дата', placeholder: '2026-10-12' },
      { name: 'note', label: 'Повестка', multiline: true },
    ],
  },
  publications: {
    heading: 'Публикация / конференция',
    fields: [
      { name: 'title', label: 'Название работы', full: true },
      { name: 'kind', label: 'Тип: журнал / конференция' },
      { name: 'note', label: 'Журнал / конференция' },
      { name: 'status_label', label: 'Статус' },
    ],
  },
};

const RESEARCH_META: Record<string, { nirLabel: string; minPublications: number | null; publicationRule: string; semester: boolean }> = {
  BACHELOR: { nirLabel: 'Исследовательский результат', minPublications: 1, publicationRule: '', semester: false },
  MASTER: { nirLabel: 'НИРМ', minPublications: 2, publicationRule: '', semester: true },
  DOCTORAL: {
    nirLabel: 'НИРД / ДҒЗЖ',
    minPublications: null,
    publicationRule: 'Требования настраиваются по действующим правилам.',
    semester: true,
  },
};

const REVIEW_CRITERIA = [
  { title: '1. Научное содержание и исследовательская логика', color: '#41d2ff', text: 'Соответствие теме и задаче · логика аргументации · методология и материал · достоверность выводов · достаточность источников.' },
  { title: '2. Академический язык и качество текста', color: '#9e80ff', text: 'Грамматика · орфография · пунктуация · лексика и терминология · академический стиль · связность и точность формулировок.' },
  { title: '3. Оформление и доказательные материалы', color: '#49ddb7', text: 'Структура · ссылки и библиография · таблицы/рисунки · приложения · соответствие образцам и техническим требованиям.' },
];

const REVIEW_FRAGMENTS = [
  { fragment: '«...полученные результаты полностью подтверждают эффективность метода...»', note: 'Формулировка выглядит слишком категоричной для представленного объёма данных. Здесь лучше уточнить, какие именно показатели подтверждают эффективность, и ограничить вывод рамками вашей выборки.' },
  { fragment: '«исследование показало значительные изменения»', note: 'В этом фрагменте необходимо указать конкретный показатель изменения или сослаться на соответствующую таблицу/расчёт, иначе вывод остаётся недоказанным.' },
  { fragment: '«все исследователи подтверждают данный подход»', note: 'Такое обобщение требует источников. Лучше назвать конкретных авторов или переформулировать тезис так, чтобы он отражал реально проанализированную литературу.' },
];

const REVIEW_DECISIONS = [
  ['ACCEPT', 'Принять'],
  ['MINOR', 'Небольшая доработка'],
  ['MAJOR', 'Существенная доработка'],
  ['REJECT', 'Не принять'],
] as const;

const ICONS: Record<string, string> = {
  dashboard: '<path d="M4 4h7v7H4zM13 4h7v4h-7zM13 10h7v10h-7zM4 13h7v7H4z"/>',
  users: '<circle cx="9" cy="8" r="3"/><circle cx="17" cy="9" r="2"/><path d="M3 20c0-4 2-7 6-7s6 3 6 7M15 14c3 0 5 2 5 5"/>',
  topic: '<path d="M4 5h16v14H4zM8 9h8M8 13h6"/>',
  plan: '<path d="M5 4h14v16H5zM8 8h8M8 12h8M8 16h5"/>',
  task: '<path d="M5 5h14v14H5zM8 10l2 2 5-5M8 16h8"/>',
  files: '<path d="M3 7h7l2 2h9v10H3z"/>',
  research: '<path d="M5 20h14M7 20V8h10v12M9 8V4h6v4M10 12h4M10 16h4"/>',
  calendar: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4M16 3v4M3 10h18"/>',
  chat: '<path d="M4 5h16v11H9l-5 4zM8 9h8M8 12h5"/>',
  shield: '<path d="M12 3l8 3v6c0 5-3 8-8 10-5-2-8-5-8-10V6zM8 12l3 3 5-6"/>',
  spark: '<path d="M12 2l2.1 5.9L20 10l-5.9 2.1L12 18l-2.1-5.9L4 10l5.9-2.1zM19 17l.8 2.2L22 20l-2.2.8L19 23l-.8-2.2L16 20l2.2-.8z"/>',
  star: '<path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.6l6.2-.9z"/>',
};

const tone = (color: string) => ({ ['--c' as string]: color });

function Mark({ name }: { name: string }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true" dangerouslySetInnerHTML={{ __html: ICONS[name] || ICONS.plan }} />;
}

function IconBlock({ name }: { name: string }) {
  return <span className="icon"><Mark name={name} /></span>;
}

const levelOf = (level: string) => LEVELS.find((item) => item.id === level);
const levelShort = (level: string) => levelOf(level)?.short || level;

function badgeClass(status: string) {
  if (['APPROVED', 'CONFIRMED', 'DONE', 'COMPLETED', 'READY', 'CAN_USE', 'CURRENT', 'ACCEPTED'].includes(status)) return 'ok';
  if (['NEEDS_REVIEW', 'UNDER_REVIEW', 'PENDING', 'ON_REVIEW', 'PENDING_SUPERVISOR', 'PENDING_REVIEW', 'WAITING', 'RETURNED', 'PROPOSED', 'ACTIVE'].includes(status)) return 'warn';
  if (['REJECTED', 'DECLINED'].includes(status)) return 'bad';
  return 'info';
}

const CHECKS = ['Содержание и научная логика', 'Техническое оформление', 'Ссылки и список литературы', 'Таблицы, рисунки и визуализации', 'Комплектность работы'];

function draftFrom(detail: Detail) {
  const intro = detail.plan.intro;
  return {
    actuality: intro?.actuality || '',
    goal: intro?.goal ?? '',
    planTasks: intro?.tasks_text ?? (intro?.tasks || []).join('\n'),
    conclusion: detail.plan.conclusion || '',
    bibliography: detail.plan.bibliography ?? '',
    kz: detail.topic.translations?.kz || '',
    ru: detail.topic.translations?.ru ?? '',
    en: detail.topic.translations?.en || '',
    consultantName: detail.consultant?.name || '',
    consultantOrg: detail.consultant?.organization || '',
    consultantDegree: detail.consultant?.degree || '',
    checks: detail.writing_checks || {},
  };
}

function initials(name: string) {
  return name.split(/\s+/).slice(0, 2).map((part) => part[0] || '').join('').toUpperCase();
}

function pickFile(onName: (name: string) => void) {
  const input = document.createElement('input');
  input.type = 'file';
  input.style.position = 'fixed';
  input.style.left = '-9999px';
  input.addEventListener('change', () => {
    const name = input.files?.[0]?.name;
    input.remove();
    if (name) onName(name);
  }, { once: true });
  document.body.append(input);
  input.click();
}

function FileRow({
  title,
  file,
  type,
  version,
  onOpen,
  onDownload,
  onReplace,
  onDelete,
}: {
  title: string;
  file: string;
  type: string;
  version: number;
  onOpen: () => void;
  onDownload: () => void;
  onReplace: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="sv121-file">
      <span className="fi"><Mark name="files" /></span>
      <div>
        <strong>{title}</strong>
        <small>{file || 'Файл не загружен'} · {type || 'FILE'} · v{version || 1}</small>
      </div>
      <div className="sv121-file-actions">
        <button type="button" onClick={onOpen}>Открыть</button>
        <button type="button" onClick={onDownload}>Скачать</button>
        <button type="button" onClick={onReplace}>Заменить</button>
        <button type="button" className="danger" onClick={onDelete}>Удалить</button>
      </div>
    </div>
  );
}

function isPublication(kind: string) {
  return /пуб|pub|article|conference|конф|журнал/i.test(kind);
}

function isPractice(kind: string, title: string) {
  return /practice|практик|стаж|internship/i.test(`${kind} ${title}`);
}

export const SupervisionWorkspace = ({ onClose }: { onClose: () => void }) => {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const requestedStudent = params.get('student');
  const requestedSection = params.get('section');
  const openedDeepLink = useRef(false);
  const ownerPreview = user?.roles?.includes('SYSTEM_OWNER');
  const supervisorName = ownerPreview ? 'Syrym Zhanzhigitov' : user?.fullName || 'Научный руководитель';
  const degree = ownerPreview ? 'доктор философии (PhD)' : user?.position || 'Научный руководитель';
  const position = ownerPreview
    ? 'и.о. ассоциированного профессора кафедры казахского языкознания'
    : user?.department || 'Кафедра научного руководства';
  const academiaId = ownerPreview ? 'ZA000001' : user?.academiaId || '—';
  const [view, setView] = useState<ViewId>(
    requestedStudent && (NAV.some((item) => item.id === requestedSection) || requestedSection === 'tasks' || requestedSection === 'card')
      ? (requestedSection as ViewId)
      : 'overview',
  );
  const [level, setLevel] = useState<'all' | 'BACHELOR' | 'MASTER' | 'DOCTORAL'>('all');
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [notice, setNotice] = useState('');
  const [meetingCount, setMeetingCount] = useState(0);
  const [quickAi, setQuickAi] = useState('Выберите действие — AI сформирует проект, который руководитель обязан проверить и утвердить.');
  const [composer, setComposer] = useState<ComposerKind | null>(null);
  const saveDraftRef = useRef<(() => Promise<void>) | null>(null);

  const loadStudents = useCallback(async () => {
    const payload = await supervisorApi.module('supervision', { tab: 'students' });
    setStudents((payload.items as StudentRow[] | undefined) ?? []);
  }, []);

  const openStudent = async (id: string, nextView: ViewId = 'card') => {
    setSelectedId(id);
    setView(nextView);
    const payload = await supervisorApi.studentDetail(id);
    setDetail(payload as Detail);
  };

  const ensureDetail = async (nextView: ViewId) => {
    setView(nextView);
    const id = selectedId || students[0]?.id;
    if (!id) return;
    if (detail?.student.id === id && selectedId) return;
    await openStudent(id, nextView);
  };

  const saveStudent = async (body: { topic?: string; review_status?: string; plan_status?: string; defence_status?: string }) => {
    if (!selectedId) return;
    await supervisorApi.updateStudent(selectedId, body);
    await openStudent(selectedId, view);
    await loadStudents();
    setNotice('Решение сохранено');
  };

  const decideItem = async (collection: string, itemId: string, status: string, message = 'Решение сохранено') => {
    if (!selectedId) return;
    const payload = await supervisorApi.decideItem(selectedId, { collection, item_id: itemId, status });
    setDetail(payload as Detail);
    setNotice(message);
  };

  const attachResearchFile = (itemId: string, title: string) => {
    if (!selectedId) return;
    pickFile((fileName) => {
      void applyDetail(
        () => supervisorApi.attachResearchFile(selectedId, itemId, fileName),
        `Добавлен файл публикации ${title}`,
      );
    });
  };

  const askAi = async (caption: string) => {
    setQuickAi(caption);
    const id = selectedId || students[0]?.id;
    if (!id) return;
    if (!selectedId) setSelectedId(id);
    try {
      const advice = await supervisorApi.askStudent(id);
      if (!detail || detail.student.id !== id) {
        const payload = await supervisorApi.studentDetail(id);
        setDetail({ ...(payload as Detail), last_ai: advice });
      } else {
        setDetail((current) => (current ? { ...current, last_ai: advice } : current));
      }
      setQuickAi(advice.answer || caption);
      setNotice('Подсказка сохранена');
    } catch {
      setNotice('Не удалось получить подсказку.');
    }
  };

  useEffect(() => {
    let alive = true;
    loadStudents()
      .then(() => {
        if (!alive || openedDeepLink.current || !requestedStudent) return;
        openedDeepLink.current = true;
        const section = NAV.some((item) => item.id === requestedSection) || requestedSection === 'tasks' || requestedSection === 'card'
          ? (requestedSection as ViewId)
          : 'plan';
        void openStudent(requestedStudent, section);
      })
      .catch(() => setNotice('Не удалось открыть обучающихся.'));
    supervisorApi.meetings().then((rows) => setMeetingCount(rows.length)).catch(() => setMeetingCount(0));
    return () => {
      alive = false;
    };
  }, [loadStudents, requestedSection, requestedStudent]);

  const visible = useMemo(
    () => students.filter((item) => level === 'all' || item.education_level === level),
    [level, students],
  );
  const ranked = [...students].sort((a, b) => (b.rating_score ?? b.progress_pct) - (a.rating_score ?? a.progress_pct));
  const average = students.length
    ? Math.round(students.reduce((sum, item) => sum + item.progress_pct, 0) / students.length)
    : 0;
  const onReview = students.filter((item) => ['NEEDS_REVIEW', 'UNDER_REVIEW'].includes(item.review_status) || item.plan_status === 'UNDER_REVIEW').length;
  const topicsWaiting = students.filter((item) => item.review_status !== 'APPROVED').length;
  const plansWaiting = students.filter((item) => item.plan_status !== 'CONFIRMED').length;

  const applyDetail = async (run: () => Promise<Record<string, unknown>>, ok: string) => {
    try {
      const payload = await run();
      setDetail(payload as Detail);
      setNotice(ok);
    } catch (error) {
      setNotice(error instanceof ApiError ? error.message : 'Не удалось обновить документы.');
    }
  };

  const replaceLevelDoc = (doc: LevelDocument) => {
    if (!selectedId) return;
    pickFile((fileName) => {
      void applyDetail(
        () => supervisorApi.replaceLevelDocument(selectedId, doc.id, fileName),
        `Документ заменён: ${doc.title}`,
      );
    });
  };

  const deleteLevelDoc = (doc: LevelDocument) => {
    if (!selectedId) return;
    void applyDetail(
      () => supervisorApi.deleteLevelDocument(selectedId, doc.id),
      'Дополнительный документ удалён',
    );
  };

  const uploadOrder = () => {
    if (!selectedId) {
      setNotice('Сначала откройте карточку обучающегося.');
      return;
    }
    pickFile((fileName) => {
      const ext = fileName.split('.').pop()?.toUpperCase() || 'PDF';
      void addEntry('order', { title: fileName, kind: ext });
    });
  };

  const addEntry = async (kind: ComposerKind, fields: Record<string, string>) => {
    if (!selectedId) {
      setNotice('Сначала откройте карточку обучающегося.');
      return;
    }
    const title = fields.title.trim();
    if (!title) {
      setNotice('Укажите название.');
      return;
    }
    if (kind === 'documents') {
      setComposer(null);
      pickFile((fileName) => {
        void applyDetail(
          () => supervisorApi.addLevelDocument(selectedId, {
            title,
            file_type: fields.kind?.trim() || 'PDF / DOCX',
            file_name: fileName,
          }),
          `Добавлен общий материал: ${title}`,
        );
      });
      return;
    }
    try {
      const payload = await supervisorApi.addEntry(selectedId, {
        collection: kind,
        title,
        kind: fields.kind?.trim() || undefined,
        note: fields.note?.trim() || undefined,
        date: fields.date?.trim() || undefined,
        deadline: fields.deadline?.trim() || undefined,
        status_label: fields.status_label?.trim() || undefined,
      });
      setDetail(payload as Detail);
      setComposer(null);
      setNotice(kind === 'order'
        ? 'Приказ о назначении руководителя и теме обновлён'
        : kind === 'publications'
          ? 'Добавлена публикация / конференция'
          : 'Запись сохранена');
      if (kind === 'consultations') {
        const rows = await supervisorApi.meetings();
        setMeetingCount(rows.length);
      }
    } catch {
      setNotice('Не удалось сохранить запись.');
    }
  };

  const saveDraft = async (body: Parameters<typeof supervisorApi.saveDraft>[1]) => {
    if (!selectedId) {
      setNotice('Сначала откройте карточку обучающегося.');
      return;
    }
    try {
      const payload = await supervisorApi.saveDraft(selectedId, body);
      setDetail(payload as Detail);
      setNotice('Черновик сохранён. Обучающийся увидит изменения после публикации.');
    } catch {
      setNotice('Не удалось сохранить изменения.');
    }
  };

  const publishDraft = async (body: Parameters<typeof supervisorApi.saveDraft>[1]) => {
    if (!selectedId) return;
    try {
      const saved = await supervisorApi.saveDraft(selectedId, body);
      const published = await supervisorApi.publishDraft(selectedId, saved.draft_revision as number);
      setDetail(published as Detail);
      await loadStudents();
      setNotice('План и материалы опубликованы обучающемуся');
    } catch (error) {
      setNotice(error instanceof ApiError ? error.message : 'Не удалось опубликовать изменения.');
    }
  };

  const assignTopic = async (topicId: string) => {
    if (!selectedId) return;
    try {
      const payload = await supervisorApi.assignTopic(selectedId, topicId);
      setDetail(payload as Detail);
      await loadStudents();
      setNotice('Тема назначена и утверждена');
    } catch (error) {
      setNotice(error instanceof ApiError ? error.message : 'Не удалось назначить тему.');
    }
  };

  const selectNav = (id: ViewId) => {
    if (id === 'overview' || id === 'students') {
      setView(id);
      return;
    }
    void ensureDetail(id);
  };

  const insideStudent = view !== 'overview' && view !== 'students';

  return (
    <div className="sv121-root" role="dialog" aria-modal="true">
      <div className="sv121-shell">
        <header className="sv121-top">
          <button type="button" className="back" onClick={onClose} aria-label="Назад">← Назад</button>
          <div className="sv121-brand">
            <small>Личное пространство научного руководителя</small>
            <h1>Научное руководство</h1>
            <p>Обучающиеся · темы · планы · задачи · совместная литература · документы · публикации и НИР · практика · консультации · предзащита и защита.</p>
          </div>
          <div className="sv121-top-actions">
            <button
              type="button"
              className="sv121-btn gold"
              onClick={() => {
                if (saveDraftRef.current) void saveDraftRef.current();
                else setNotice('Откройте карточку обучающегося, чтобы сохранить поля.');
              }}
            >
              Сохранить черновик
            </button>
            <button type="button" className="sv121-btn" onClick={onClose} aria-label="Закрыть">Закрыть ×</button>
          </div>
        </header>
        <div className="sv121-body">
          <aside className="sv121-side">
            <div className="sv121-rolebox">
              <small>Научный руководитель</small>
              <strong>{supervisorName}</strong>
              <p>{degree}<br />{position}</p>
            </div>
            <nav className="sv121-nav">
              {NAV.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  className={view === item.id || (view === 'card' && item.id === 'students') || (view === 'tasks' && item.id === 'plan') ? 'active' : ''}
                  style={tone(NAV_COLORS[index % NAV_COLORS.length])}
                  onClick={() => selectNav(item.id)}
                >
                  <span className="ico"><Mark name={item.icon} /></span>
                  <span>
                    <strong>{item.label}</strong>
                    <small>{item.hint}</small>
                  </span>
                  <span className="go">›</span>
                </button>
              ))}
            </nav>
          </aside>
          <main className="sv121-main">
            {notice ? <div className="sv121-callout" style={{ ...tone('#49ddb7'), marginBottom: 12 }}>{notice}</div> : null}
            {!insideStudent && (
              <section className="sv121-hero">
                <article className="sv121-panel" style={tone('#49ddb7')}>
                  <span className="sv121-eyebrow">Управление научной работой</span>
                  <h2>Управление исследовательскими работами</h2>
                  <p>От первого приглашения и выбора темы до защиты, публикаций и архива. Каждый обучающийся видит только собственный рабочий контур.</p>
                  <div className="sv121-kpis">
                    <div className="sv121-kpi"><small>Подопечных</small><b>{students.length}</b><em>3 уровня</em></div>
                    <div className="sv121-kpi"><small>На проверке</small><b>{onReview}</b><em>Задания и планы</em></div>
                    <div className="sv121-kpi"><small>Консультации</small><b>{meetingCount}</b><em>Запланированы</em></div>
                    <div className="sv121-kpi"><small>Средний прогресс</small><b>{average}%</b><em>По активным этапам</em></div>
                  </div>
                </article>
                <article className="sv121-panel" style={tone('#9e80ff')}>
                  <span className="sv121-eyebrow">НАУЧНЫЙ РУКОВОДИТЕЛЬ</span>
                  <h3>{supervisorName}</h3>
                  <p><b>{degree}</b><br />{position}</p>
                  <div className="sv121-actions">
                    <span className="sv121-badge ok">● ACTIVE</span>
                    <span className="sv121-badge info">Academia ID {academiaId}</span>
                  </div>
                </article>
              </section>
            )}

            {view === 'overview' && (
              <section className="sv121-grid">
                <article className="sv121-card sv121-span8" style={tone('#41d2ff')}>
                  <header>
                    <div>
                      <span className="sv121-eyebrow">МОИ ОБУЧАЮЩИЕСЯ</span>
                      <h3>Три уровня научного руководства</h3>
                      <p>Один центр управления, но отдельные правила, документы и хронология для каждого уровня.</p>
                    </div>
                    <IconBlock name="users" />
                  </header>
                  <div className="sv121-levels" style={{ marginTop: 14 }}>
                    {LEVELS.map((item, index) => {
                      const count = students.filter((row) => row.education_level === item.id).length;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          className="sv121-level"
                          style={tone(item.accent)}
                          onClick={() => {
                            setLevel(item.id);
                            setView('students');
                          }}
                        >
                          <span className="num">0{index + 1}</span>
                          <h3>{item.short}</h3>
                          <p>{item.duration}. Подопечных: {count}</p>
                          <div className="meta">
                            <span className="sv121-badge info">{item.programs} программы</span>
                            <span className="sv121-badge ok">{item.nir}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </article>
                <article className="sv121-card sv121-span4" style={tone('#e8b33b')}>
                  <header>
                    <div>
                      <span className="sv121-eyebrow">РЕЙТИНГ ПОДОПЕЧНЫХ</span>
                      <h3>Своевременность и прогресс</h3>
                      <p>Не расходуемые баллы, а показатель качества сопровождения и выполнения плана.</p>
                    </div>
                    <IconBlock name="star" />
                  </header>
                  <div className="sv121-list" style={{ marginTop: 12 }}>
                    {ranked.slice(0, 5).map((row, index) => (
                      <div className="sv121-row" key={row.id}>
                        <div>
                          <strong>{index + 1}. {row.full_name}</strong>
                          <small>{levelShort(row.education_level)} · {row.program}</small>
                        </div>
                        <div className="right">
                          <b>{row.rating_score ?? row.progress_pct}</b>
                          <small>из 100</small>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="sv121-actions">
                    <button type="button" className="sv121-btn" onClick={() => { setLevel('all'); setView('students'); }}>Открыть всех →</button>
                  </div>
                </article>
                <article className="sv121-card sv121-span6" style={tone('#49ddb7')}>
                  <header>
                    <div>
                      <span className="sv121-eyebrow">КОНТРОЛЬ СЕГОДНЯ</span>
                      <h3>Что требует внимания</h3>
                    </div>
                    <IconBlock name="task" />
                  </header>
                  <div className="sv121-list" style={{ marginTop: 12 }}>
                    <div className="sv121-row">
                      <div><strong>{topicsWaiting} ответа по темам</strong><small>Нужно проверить обоснование и вопросы</small></div>
                      <span className="sv121-badge warn">Сегодня</span>
                    </div>
                    <div className="sv121-row">
                      <div><strong>{plansWaiting} задания на проверке</strong><small>Версии файлов и комментарии обучающихся</small></div>
                      <span className="sv121-badge bad">Action</span>
                    </div>
                    <div className="sv121-row">
                      <div><strong>{meetingCount} консультаций</strong><small>План публикаций и ближайшие встречи</small></div>
                      <span className="sv121-badge info">План</span>
                    </div>
                  </div>
                </article>
                <article className="sv121-card sv121-span6 sv121-ai" style={tone('#9e80ff')}>
                  <header>
                    <div>
                      <span className="sv121-eyebrow">ИИ-помощник</span>
                      <h3>Помощник научного руководителя</h3>
                      <p>Темы, вопросы, структура плана, разбивка задачи и риск-контроль. Проект проверяет руководитель.</p>
                    </div>
                    <IconBlock name="spark" />
                  </header>
                  <div className="sv121-actions">
                    <button type="button" className="sv121-btn violet" onClick={() => void askAi('Проект тем подготовлен. Проверьте формулировки перед отправкой обучающемуся.')}>Предложить темы</button>
                    <button type="button" className="sv121-btn violet" onClick={() => void askAi('Проект вопросов по исследовательскому замыслу подготовлен.')}>Сформировать вопросы</button>
                    <button type="button" className="sv121-btn violet" onClick={() => void askAi('Риск-контроль: темы без решения, планы на проверке и ближайшие консультации.')}>Показать риски</button>
                  </div>
                  <div className="sv121-ai-output">{quickAi}</div>
                </article>
              </section>
            )}

            {view === 'students' && (
              <section className="sv121-panel" style={tone('#49ddb7')}>
                <div className="sv121-tabs">
                  <button type="button" className={level === 'all' ? 'active' : ''} onClick={() => setLevel('all')}>Все</button>
                  {LEVELS.map((item) => (
                    <button key={item.id} type="button" className={level === item.id ? 'active' : ''} onClick={() => setLevel(item.id)}>
                      {item.short}
                    </button>
                  ))}
                </div>
                <table className="sv121-table">
                  <thead>
                    <tr>
                      <th>#</th><th>Обучающийся</th><th>Уровень</th><th>Программа</th><th>Прогресс</th><th>Рейтинг</th><th>Ближайшее</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((row, index) => (
                      <tr key={row.id} style={tone(levelOf(row.education_level)?.accent || '#41d2ff')}>
                        <td>{index + 1}</td>
                        <td><strong>{row.full_name}</strong><br /><small>{row.academia_id}</small></td>
                        <td>{levelShort(row.education_level)}<br />{row.course_year || '—'} курс</td>
                        <td>{row.program}</td>
                        <td>
                          <b>{row.progress_pct}%</b>
                          <div className="sv121-progress" style={{ ['--p' as string]: `${row.progress_pct}%` }}><span /></div>
                        </td>
                        <td><b>{row.rating_score ?? row.progress_pct}</b>/100</td>
                        <td>{row.topic || 'Тема на согласовании'}</td>
                        <td><button type="button" onClick={() => void openStudent(row.id)}>Открыть</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            {insideStudent && detail && (
              <StudentScreens
                view={view}
                detail={detail}
                supervisorName={supervisorName}
                degree={degree}
                position={position}
                onOpen={selectNav}
                onBack={() => setView('students')}
                onSave={saveStudent}
                onDecide={decideItem}
                onAttachFile={attachResearchFile}
                onAsk={askAi}
                onCompose={setComposer}
                onNotice={setNotice}
                onReplaceLevelDoc={replaceLevelDoc}
                onDeleteLevelDoc={deleteLevelDoc}
                onUploadOrder={uploadOrder}
                saveRef={saveDraftRef}
                onSaveDraft={saveDraft}
                onPublishDraft={publishDraft}
                onAssignTopic={assignTopic}
              />
            )}
            {insideStudent && !detail && <div className="sv121-empty">Выберите обучающегося в списке.</div>}
          </main>
        </div>
      </div>
      {composer ? (
        <EntryDialog
          kind={composer}
          onClose={() => setComposer(null)}
          onSubmit={(fields) => void addEntry(composer, fields)}
        />
      ) : null}
    </div>
  );
};

function EntryDialog({
  kind,
  onClose,
  onSubmit,
}: {
  kind: ComposerKind;
  onClose: () => void;
  onSubmit: (fields: Record<string, string>) => void;
}) {
  const form = COMPOSERS[kind];
  const [fields, setFields] = useState<Record<string, string>>(
    kind === 'documents'
      ? { kind: 'PDF / DOCX' }
      : kind === 'publications'
        ? { kind: 'Конференция', status_label: 'Планируется' }
        : {},
  );
  return (
    <div className="sv121-dialog open" role="dialog" aria-modal="true">
      <div className="sv121-dialog-box">
        <header>
          <h2>{form.heading}</h2>
          <button type="button" onClick={onClose} aria-label="Закрыть">×</button>
        </header>
        <div className="sv121-formgrid" style={{ marginTop: 14 }}>
          {form.fields.map((field) => (
            <div key={field.name} className={`sv121-field${field.multiline || field.full ? ' full' : ''}`}>
              <label>{field.label}</label>
              {field.multiline ? (
                <textarea
                  value={fields[field.name] || ''}
                  placeholder={field.placeholder}
                  onChange={(event) => setFields((current) => ({ ...current, [field.name]: event.target.value }))}
                />
              ) : (
                <input
                  value={fields[field.name] || ''}
                  placeholder={field.placeholder}
                  onChange={(event) => setFields((current) => ({ ...current, [field.name]: event.target.value }))}
                />
              )}
            </div>
          ))}
        </div>
        <div className="sv121-actions">
          <button type="button" className="sv121-btn gold" onClick={() => onSubmit(fields)}>Сохранить</button>
          <button type="button" className="sv121-btn" onClick={onClose}>Отмена</button>
        </div>
      </div>
    </div>
  );
}

function StudentScreens({
  view,
  detail,
  supervisorName,
  degree,
  position,
  onOpen,
  onBack,
  onSave,
  onDecide,
  onAttachFile,
  onAsk,
  onCompose,
  onNotice,
  onReplaceLevelDoc,
  onDeleteLevelDoc,
  onUploadOrder,
  saveRef,
  onSaveDraft,
  onPublishDraft,
  onAssignTopic,
}: {
  view: ViewId;
  detail: Detail;
  supervisorName: string;
  degree: string;
  position: string;
  onOpen: (view: ViewId) => void;
  onBack: () => void;
  onSave: (body: { topic?: string; review_status?: string; plan_status?: string; defence_status?: string }) => Promise<void>;
  onDecide: (collection: string, itemId: string, status: string, message?: string) => Promise<void>;
  onAttachFile: (itemId: string, title: string) => void;
  onAsk: (caption: string) => Promise<void>;
  onCompose: (kind: ComposerKind) => void;
  onNotice: (text: string) => void;
  onReplaceLevelDoc: (doc: LevelDocument) => void;
  onDeleteLevelDoc: (doc: LevelDocument) => void;
  onUploadOrder: () => void;
  saveRef: { current: (() => Promise<void>) | null };
  onSaveDraft: (body: Parameters<typeof supervisorApi.saveDraft>[1]) => Promise<void>;
  onPublishDraft: (body: Parameters<typeof supervisorApi.saveDraft>[1]) => Promise<void>;
  onAssignTopic: (topicId: string) => Promise<void>;
}) {
  const student = detail.student;
  const level = levelOf(student.education_level);
  const accent = level?.accent || '#49ddb7';
  const next = detail.deadlines.find((item) => item.status !== 'DONE');
  const reviewCount = detail.plan.tasks.filter((item) => ['ON_REVIEW', 'SUBMITTED', 'UNDER_REVIEW', 'PENDING'].includes(item.status)).length;
  const activeTasks = detail.plan.tasks.filter((item) => item.status !== 'APPROVED').slice(0, 3);
  const lastMeeting = detail.consultations[0];
  const publications = detail.nir.filter((item) => isPublication(item.kind));
  const practices = detail.nir.filter((item) => isPractice(item.kind, item.title));
  const reports = detail.nir.filter((item) => !isPublication(item.kind) && !isPractice(item.kind, item.title));
  const researchMeta = RESEARCH_META[student.education_level] || RESEARCH_META.BACHELOR;
  const publicationRule = researchMeta.minPublications == null
    ? researchMeta.publicationRule
    : `Минимум публикаций / апробаций в текущем прототипе — ${researchMeta.minPublications}.`;
  const approvedTopic = student.review_status === 'APPROVED';
  const [draft, setDraft] = useState(() => draftFrom(detail));
  const [review, setReview] = useState<{
    itemId: string;
    mode: 'manual' | 'standard' | 'ai' | null;
    status: 'DRAFT' | 'CONFIRMED' | 'APPROVED' | 'SENT';
    decision: string;
    summary: string;
    files: string[];
  } | null>(null);
  useEffect(() => {
    setDraft(draftFrom(detail));
  }, [detail]);
  useEffect(() => () => {
    saveRef.current = null;
  }, [saveRef]);
  const draftPayload = () => ({
    actuality: draft.actuality,
    goal: draft.goal,
    plan_tasks: draft.planTasks,
    conclusion: draft.conclusion,
    bibliography: draft.bibliography,
    translation_kz: draft.kz,
    translation_ru: draft.ru,
    translation_en: draft.en,
    consultant_name: draft.consultantName,
    consultant_organization: draft.consultantOrg,
    consultant_degree: draft.consultantDegree,
    writing_checks: draft.checks,
  });
  saveRef.current = () => onSaveDraft(draftPayload());

  return (
    <>
      <section className="sv122-student-context" style={tone(accent)}>
        <div className="sv122-breadcrumb">
          <span>Научное руководство</span>
          <span className="sep">›</span>
          <span>{level?.short || student.education_level}</span>
          <span className="sep">›</span>
          <b>{student.full_name}</b>
          <span className="sv121-badge info">{student.course_year || '—'} курс</span>
        </div>
        <div className="sv122-student-head">
          <div className="sv122-person">
            <span className="sv122-avatar">{initials(student.full_name)}</span>
            <div>
              <span className="sv121-eyebrow">КАРТОЧКА ОБУЧАЮЩЕГОСЯ · {student.academia_id}</span>
              <h2>{student.full_name}</h2>
              <p>
                {student.program} · {level?.label} · {level?.duration}
                <br />
                {approvedTopic && student.topic ? `Тема: ${student.topic}` : 'Тема находится на этапе согласования'}
              </p>
            </div>
          </div>
          <div className="sv122-mini">
            <small>Прогресс</small>
            <b>{student.progress_pct}%</b>
            <em>по этапам и задачам</em>
          </div>
          <div className="sv122-mini">
            <small>Рейтинг</small>
            <b>{student.rating_score ?? student.progress_pct}/100</b>
            <em>рабочий показатель</em>
          </div>
          <div className="sv122-mini">
            <small>Ближайшее</small>
            <b>{reviewCount ? `${reviewCount} на проверке` : next ? '1 этап' : '✓'}</b>
            <em>{next?.title || 'План закрыт'}</em>
          </div>
        </div>
        <div className="sv122-student-tabs" style={tone(accent)}>
          {STUDENT_TABS.map(([id, label]) => (
            <button key={id} type="button" className={view === id ? 'active' : ''} onClick={() => onOpen(id)}>
              {label}
            </button>
          ))}
          <button type="button" className="sv122-backlist" onClick={onBack}>← К списку обучающихся</button>
        </div>
      </section>

      {view === 'card' && (
        <section className="sv122-summary-grid">
          <article className="sv121-card wide" style={tone(accent)}>
            <header>
              <div>
                <span className="sv121-eyebrow">ТЕКУЩАЯ НАУЧНАЯ РАБОТА</span>
                <h3>{approvedTopic && student.topic ? student.topic : 'Тема ещё не утверждена'}</h3>
                <p>{approvedTopic ? 'Утверждённая тема является главным контекстом плана, задач, материалов и сроков.' : 'Сейчас обучающийся находится на этапе выбора/согласования темы.'}</p>
              </div>
              <IconBlock name="topic" />
            </header>
            <div className="sv121-actions">
              <button type="button" className="sv121-btn gold" onClick={() => onOpen('topic')}>Открыть тему</button>
              <button type="button" className="sv121-btn" onClick={() => onOpen('plan')}>План работы</button>
              <button type="button" className="sv121-btn" onClick={() => onOpen('tasks')}>Задачи</button>
            </div>
          </article>
          <article className="sv121-card side" style={tone('#e8b33b')}>
            <header>
              <div>
                <span className="sv121-eyebrow">БЛИЖАЙШИЙ ЭТАП</span>
                <h3>{next?.title || 'Маршрут завершён'}</h3>
                <p>{next ? `${next.date} · ${next.status}` : 'Все этапы текущего маршрута закрыты.'}</p>
              </div>
              <IconBlock name="calendar" />
            </header>
            <div className="sv121-actions">
              <button type="button" className="sv121-btn" onClick={() => onOpen('timeline')}>Открыть хронологию</button>
            </div>
          </article>
          <article className="sv121-card wide" style={tone('#49ddb7')}>
            <header>
              <div>
                <span className="sv121-eyebrow">АКТИВНЫЕ ЗАДАЧИ</span>
                <h3>{activeTasks.length} ближайших рабочих действий</h3>
                <p>Все задачи привязаны к плану, сроку и ожидаемому результату.</p>
              </div>
              <IconBlock name="task" />
            </header>
            <div className="sv121-list" style={{ marginTop: 10 }}>
              {activeTasks.length ? activeTasks.map((item) => (
                <div className="sv121-row" key={item.id}>
                  <div><strong>{item.title}</strong><small>{item.deadline} · {item.status}</small></div>
                  <span className={`sv121-badge ${badgeClass(item.status)}`}>{item.status}</span>
                </div>
              )) : <div className="sv121-empty">Нет активных задач</div>}
            </div>
            <div className="sv121-actions">
              <button type="button" className="sv121-btn" onClick={() => onOpen('tasks')}>Все задачи →</button>
            </div>
          </article>
          <article className="sv121-card side" style={tone('#9e80ff')}>
            <header>
              <div>
                <span className="sv121-eyebrow">ПОСЛЕДНЯЯ КОНСУЛЬТАЦИЯ</span>
                <h3>{lastMeeting?.title || 'Консультаций пока нет'}</h3>
                <p>{lastMeeting ? `${lastMeeting.date} · ${lastMeeting.status}` : 'Можно назначить первую встречу-консультацию.'}</p>
              </div>
              <IconBlock name="chat" />
            </header>
            <div className="sv121-actions">
              <button type="button" className="sv121-btn violet" onClick={() => onOpen('meetings')}>Открыть консультации</button>
            </div>
          </article>
          <article className="sv121-card sv121-span4" style={tone('#66b7e8')}>
            <header>
              <div>
                <span className="sv121-eyebrow">СОВМЕСТНАЯ ЛИТЕРАТУРА</span>
                <h3>{detail.literature.filter((item) => item.status === 'APPROVED').length} разрешённых источников</h3>
                <p>Источники руководителя доступны сразу; предложения обучающегося проходят согласование.</p>
              </div>
              <IconBlock name="files" />
            </header>
            <div className="sv121-actions">
              <button type="button" className="sv121-btn" onClick={() => onOpen('literature')}>Открыть литературу</button>
            </div>
          </article>
          <article className="sv121-card sv121-span4" style={tone('#e8b33b')}>
            <header>
              <div>
                <span className="sv121-eyebrow">ПЛАН → ЗАДАЧИ</span>
                <h3>{detail.plan.tasks.filter((item) => item.status === 'APPROVED').length}/{detail.plan.tasks.length} задач принято</h3>
                <p>Количество задач не равно количеству подразделов: задачи создаются по смыслу и хронологии.</p>
              </div>
              <IconBlock name="task" />
            </header>
            <div className="sv121-actions">
              <button type="button" className="sv121-btn" onClick={() => onOpen('tasks')}>Рабочие задачи</button>
            </div>
          </article>
          <article className="sv121-card sv121-span4" style={tone('#49ddb7')}>
            <header>
              <div>
                <span className="sv121-eyebrow">ОСНОВНАЯ РАБОТА</span>
                <h3>{detail.thesis.filter((item) => item.status === 'APPROVED').length}/{detail.thesis.length} блоков принято</h3>
                <p>Структура формируется из утверждённого плана и синхронизируется у обеих сторон.</p>
              </div>
              <IconBlock name="plan" />
            </header>
            <div className="sv121-actions">
              <button type="button" className="sv121-btn" onClick={() => onOpen('writing')}>Открыть основной этап</button>
            </div>
          </article>
        </section>
      )}

      {view === 'topic' && (
        <section className="sv121-panel" style={tone(accent)}>
          <span className="sv121-eyebrow">ЭТАП 1 · ВЫБОР И УТВЕРЖДЕНИЕ ТЕМЫ</span>
          <h2 style={{ marginTop: 5 }}>Тема исследования</h2>
          <p>Руководитель предлагает темы или назначает конкретную тему. Собственное предложение обучающегося требует решения руководителя.</p>
          <div className="sv121-grid" style={{ marginTop: 14 }}>
            {detail.topic.variants.map((item, index) => (
              <article key={item.id} className="sv121-card sv121-span6" style={tone(TOPIC_COLORS[index % TOPIC_COLORS.length])}>
                <header>
                  <div>
                    <span className="sv121-eyebrow">ТЕМА {index + 1}</span>
                    <h3>{item.title}</h3>
                    <p>{item.rationale || item.status}</p>
                  </div>
                  <IconBlock name="topic" />
                </header>
                <div className="sv121-callout" style={{ ...tone('#49ddb7'), marginTop: 12 }}>
                  <b>Что будет сделано:</b> {item.result || detail.topic.title}
                </div>
                <div className="sv121-actions">
                  <span className={`sv121-badge ${badgeClass(item.status)}`}>{item.status}</span>
                  <button type="button" className="sv121-btn mint" onClick={() => void onAssignTopic(item.id)}>Назначить и утвердить эту тему</button>
                </div>
              </article>
            ))}
          </div>
          <div className="sv121-actions">
            <button type="button" className="sv121-btn gold" onClick={() => onCompose('topics')}>+ Добавить тему</button>
            <button type="button" className="sv121-btn" onClick={() => onCompose('questions')}>+ Задать вопрос обучающемуся</button>
          </div>
          <article className="sv121-card" style={{ ...tone('#9e80ff'), marginTop: 14 }}>
            <span className="sv121-eyebrow">ВОПРОСЫ РУКОВОДИТЕЛЯ</span>
            <h3>Проверка исследовательского замысла</h3>
            <div className="sv121-list" style={{ marginTop: 10 }}>
              {(detail.topic.questions || []).length ? (detail.topic.questions || []).map((item) => (
                <div className="sv121-row" key={item.id}>
                  <div><strong>{item.q}</strong><small>{item.a || 'Ответ ещё не дан'}</small></div>
                  <span className={`sv121-badge ${item.a ? 'ok' : 'warn'}`}>{item.a ? 'Ответ получен' : 'Ожидание'}</span>
                </div>
              )) : <div className="sv121-empty">Вопросов пока нет</div>}
            </div>
          </article>
          <article className="sv121-card" style={{ ...tone('#49ddb7'), marginTop: 14 }}>
            <span className="sv121-eyebrow">РЕШЕНИЕ РУКОВОДИТЕЛЯ</span>
            <h3>{approvedTopic ? 'Тема утверждена' : 'Требуется решение'}</h3>
            <div className="sv121-actions">
              <span>Для назначения выберите предложение выше.</span>
              <button type="button" className="sv121-btn danger" onClick={() => void onSave({ review_status: 'NEEDS_REVIEW' })}>Вернуть с обоснованием</button>
            </div>
          </article>
          {approvedTopic && (
            <article className="sv121-card" style={{ ...tone('#49ddb7'), marginTop: 14 }}>
              <header>
                <div>
                  <span className="sv121-eyebrow">УТВЕРЖДЁННЫЙ ПАКЕТ</span>
                  <h3>Тема на трёх языках + данные научного руководителя</h3>
                </div>
                <IconBlock name="shield" />
              </header>
              <div className="sv121-formgrid" style={{ marginTop: 12 }}>
                <div className="sv121-field full"><label>Қазақша</label><textarea value={draft.kz} onChange={(event) => setDraft((current) => ({ ...current, kz: event.target.value }))} /></div>
                <div className="sv121-field full"><label>Русский</label><textarea value={draft.ru} onChange={(event) => setDraft((current) => ({ ...current, ru: event.target.value }))} /></div>
                <div className="sv121-field full"><label>English</label><textarea value={draft.en} onChange={(event) => setDraft((current) => ({ ...current, en: event.target.value }))} /></div>
              </div>
              <div className="sv121-callout" style={{ ...tone('#9e80ff'), marginTop: 12 }}>
                <b>{supervisorName}</b><br />{degree}, {position}
              </div>
            </article>
          )}
        </section>
      )}

      {view === 'plan' && (
        <section className="sv121-panel" style={tone(accent)}>
          <span className="sv121-eyebrow">ЭТАП 2 · ПЛАН ИССЛЕДОВАНИЯ</span>
          <h2 style={{ marginTop: 5 }}>Структура работы · версия {detail.plan.version}</h2>
          <p>{level?.hint}</p>
          <div className="sv121-grid" style={{ marginTop: 14 }}>
            <article className="sv121-card sv121-span4" style={tone('#e8b33b')}>
              <h3>Введение</h3>
              <div className="sv121-field" style={{ marginTop: 10 }}><label>Актуальность</label><textarea value={draft.actuality} onChange={(event) => setDraft((current) => ({ ...current, actuality: event.target.value }))} /></div>
              <div className="sv121-field"><label>Цель</label><textarea value={draft.goal} onChange={(event) => setDraft((current) => ({ ...current, goal: event.target.value }))} /></div>
              <div className="sv121-field"><label>Задачи</label><textarea value={draft.planTasks} onChange={(event) => setDraft((current) => ({ ...current, planTasks: event.target.value }))} /></div>
            </article>
            <article className="sv121-card sv121-span8" style={tone('#41d2ff')}>
              <header>
                <div>
                  <h3>Основная часть</h3>
                  <p>2–3 крупных раздела, внутри 2–4 подраздела. Можно добавлять и перестраивать.</p>
                </div>
                <IconBlock name="plan" />
              </header>
              <div className="sv121-list" style={{ marginTop: 12 }}>
                {detail.plan.sections.map((item) => (
                  <div className="sv121-row" key={`${item.num || 'sec'}-${item.title}`}>
                    <div>
                      <strong>{item.num ? `${item.num}. ` : ''}{item.title}</strong>
                      <small>{item.subs?.join(' · ') || item.status}</small>
                    </div>
                    <span className={`sv121-badge ${badgeClass(item.status)}`}>{item.status}</span>
                  </div>
                ))}
              </div>
              <div className="sv121-actions">
                <button type="button" className="sv121-btn gold" onClick={() => onCompose('sections')}>+ Добавить раздел</button>
              </div>
            </article>
            <article className="sv121-card sv121-span6" style={tone('#49ddb7')}>
              <h3>Заключение</h3>
              <div className="sv121-field" style={{ marginTop: 10 }}><textarea value={draft.conclusion} onChange={(event) => setDraft((current) => ({ ...current, conclusion: event.target.value }))} /></div>
            </article>
            <article className="sv121-card sv121-span6" style={tone('#9e80ff')}>
              <h3>Библиография</h3>
              <p>Список литературы синхронизируется с материалами по заданиям и публикациям.</p>
              <div className="sv121-field"><textarea value={draft.bibliography} onChange={(event) => setDraft((current) => ({ ...current, bibliography: event.target.value }))} /></div>
            </article>
          </div>
          <div className="sv121-callout" style={{ ...tone('#66b7e8'), marginTop: 14 }}>
            <b>Статус плана:</b> {detail.plan.status}. После утверждения руководителем разделы и подразделы используются как контекст задач и основной работы.
          </div>
          <div className="sv121-actions">
            <button type="button" className="sv121-btn mint" onClick={() => void onPublishDraft(draftPayload())}>Утвердить и опубликовать изменения</button>
            <button type="button" className="sv121-btn" onClick={() => onOpen('tasks')}>Перейти к связанным задачам →</button>
          </div>
          {(detail.plan.versions || []).length > 0 && (
            <div className="sv121-list" style={{ marginTop: 12 }}>
              {(detail.plan.versions || []).map((item) => (
                <div className="sv121-row" key={`${item.at}-${item.label}`}>
                  <div><strong>{item.label} · {item.note}</strong><small>{item.at} · {item.actor}</small></div>
                </div>
              ))}
            </div>
          )}
          <article className="sv121-card sv161-ai-card" style={{ ...tone('#9e80ff'), marginTop: 14 }}>
            <header>
              <div>
                <span className="sv121-eyebrow">SUPER AI · ПРОЕКТ ПЛАНА</span>
                <h3>Создать или уточнить план в утверждённой форме</h3>
                <p>AI создаёт только предварительный проект — он ничего не отправляет обучающемуся автоматически.</p>
              </div>
              <IconBlock name="spark" />
            </header>
            <div className="sv161-ai-grid">
              <div className="sv121-field">
                <label>Инструкция руководителя</label>
                <textarea placeholder="Например: две части; обязательно включить педагогический эксперимент; усилить методологический раздел…" />
              </div>
              <div>
                <div className="sv121-actions">
                  <button type="button" className="sv121-btn violet" onClick={() => void onAsk('Проект структуры плана подготовлен и ожидает проверки руководителя.')}>✦ Сформировать проект</button>
                </div>
                {detail.last_ai?.answer ? <div className="sv121-ai-output">{detail.last_ai.answer}</div> : null}
              </div>
            </div>
          </article>
        </section>
      )}

      {view === 'tasks' && (
        <section className="sv121-panel" style={tone(accent)}>
          <span className="sv121-eyebrow">РАБОЧИЙ ЦИКЛ · ПЛАН → ЗАДАЧИ</span>
          <h2>Задачи создаются по смыслу и хронологии</h2>
          <p>Количество задач не обязано совпадать с количеством разделов. Одна часть плана может содержать несколько последовательных задач.</p>
          <article className="sv121-card sv161-ai-card" style={{ ...tone('#9e80ff'), marginTop: 14 }}>
            <header>
              <div>
                <span className="sv121-eyebrow">SUPER AI · НОВАЯ ЗАДАЧА</span>
                <h3>Сформировать предварительную задачу</h3>
                <p>После генерации задача остаётся черновиком руководителя.</p>
              </div>
              <IconBlock name="spark" />
            </header>
            <div className="sv161-ai-grid">
              <div className="sv121-field">
                <label>Инструкция</label>
                <textarea placeholder="Что сделать, какая дата, какой результат, объём, литература…" />
              </div>
              <div className="sv121-actions">
                <button type="button" className="sv121-btn violet" onClick={() => void onAsk('Предварительная задача подготовлена как черновик руководителя.')}>✦ Предложить задачу</button>
                <button type="button" className="sv121-btn gold" onClick={() => onCompose('tasks')}>+ Создать вручную</button>
              </div>
            </div>
          </article>
          <div className="sv121-list" style={{ marginTop: 14 }}>
            {detail.plan.tasks.map((item) => (
              <article key={item.id} className="sv121-card" style={tone(item.status === 'APPROVED' ? '#49ddb7' : item.status === 'RETURNED' ? '#ff7663' : '#e8b33b')}>
                <header>
                  <div>
                    <span className="sv121-eyebrow">{item.id} · {item.status}</span>
                    <h3>{item.title}</h3>
                    <p>
                      <b>Описание:</b> {item.desc || item.title}<br />
                      <b>Ожидаемый результат:</b> {item.result || 'По согласованному объёму'}<br />
                      <b>Дедлайн:</b> {item.deadline}
                    </p>
                  </div>
                  <IconBlock name="task" />
                </header>
                <div className="sv121-actions">
                  {item.status !== 'APPROVED' ? (
                    <button type="button" className="sv121-btn mint" onClick={() => void onDecide('tasks', item.id, 'APPROVED')}>Утвердить</button>
                  ) : <span className="sv121-badge ok">Утверждено</span>}
                  {item.status !== 'RETURNED' ? (
                    <button type="button" className="sv121-btn danger" onClick={() => void onDecide('tasks', item.id, 'RETURNED')}>Вернуть с комментарием</button>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {view === 'resources' && (
        <section className="sv121-panel" style={tone(accent)}>
          <span className="sv121-eyebrow">ОБЩИЕ МАТЕРИАЛЫ УРОВНЯ</span>
          <h2>Образцы, положения и документы · {level?.short}</h2>
          <p>Руководитель загружает, заменяет, удаляет и добавляет документы один раз для уровня. Актуальная версия автоматически видна всем моим обучающимся этого уровня.</p>
          <div className="sv121-actions">
            <button type="button" className="sv121-btn gold" onClick={() => onCompose('documents')}>+ Добавить общий документ уровня</button>
          </div>
          <div className="sv121-list" style={{ marginTop: 14 }}>
            {(detail.level_documents || []).map((item) => (
              <FileRow
                key={item.id}
                title={item.title}
                file={item.file}
                type={item.type}
                version={item.version}
                onOpen={() => onNotice('Файл открыт в защищённом предпросмотре')}
                onDownload={() => onNotice('Подготовлено безопасное скачивание файла')}
                onReplace={() => onReplaceLevelDoc(item)}
                onDelete={() => onDeleteLevelDoc(item)}
              />
            ))}
          </div>
          <article className="sv121-card" style={{ ...tone('#e8b33b'), marginTop: 14 }}>
            <header>
              <div>
                <span className="sv121-eyebrow">ПЕРСОНАЛЬНЫЙ ПРИКАЗ</span>
                <h3>Приказ о назначении научного руководителя и утверждении темы</h3>
                <p>Хранится только в карточке конкретного обучающегося и не смешивается с общими образцами уровня.</p>
              </div>
              <IconBlock name="shield" />
            </header>
            {detail.order ? (
              <div style={{ marginTop: 10 }}>
                <FileRow
                  title="Приказ обучающегося"
                  file={detail.order.title}
                  type={detail.order.kind || 'PDF'}
                  version={1}
                  onOpen={() => onNotice('Файл открыт в защищённом предпросмотре')}
                  onDownload={() => onNotice('Подготовлено безопасное скачивание файла')}
                  onReplace={onUploadOrder}
                  onDelete={() => onNotice('Канонический образец уровня нельзя удалить; его можно заменить новой версией')}
                />
              </div>
            ) : <div className="sv121-empty" style={{ marginTop: 10 }}>Приказ ещё не загружен</div>}
            <div className="sv121-actions">
              <button type="button" className="sv121-btn gold" onClick={onUploadOrder}>Загрузить / заменить приказ</button>
            </div>
          </article>
          {student.education_level === 'DOCTORAL' && (
            <article className="sv121-card" style={{ ...tone('#9e80ff'), marginTop: 14 }}>
              <header>
                <div>
                  <span className="sv121-eyebrow">Совместная работа с докторантом</span>
                  <h3>Зарубежный консультант</h3>
                  <p>Этот блок может обновлять и докторант, и научный руководитель. Новая версия сразу видна второй стороне.</p>
                </div>
                <IconBlock name="users" />
              </header>
              <div className="sv121-formgrid" style={{ marginTop: 12 }}>
                <div className="sv121-field"><label>ФИО</label><input value={draft.consultantName} onChange={(event) => setDraft((current) => ({ ...current, consultantName: event.target.value }))} /></div>
                <div className="sv121-field"><label>Организация</label><input value={draft.consultantOrg} onChange={(event) => setDraft((current) => ({ ...current, consultantOrg: event.target.value }))} /></div>
                <div className="sv121-field full"><label>Степень / должность</label><input value={draft.consultantDegree} onChange={(event) => setDraft((current) => ({ ...current, consultantDegree: event.target.value }))} /></div>
              </div>
            </article>
          )}
        </section>
      )}

      {view === 'research' && (
        <>
          <article className="sv121-card" style={tone('#e8b33b')}>
            <header>
              <div>
                <span className="sv121-eyebrow">НИР / ПРАКТИКИ / СТАЖИРОВКА · ЕДИНЫЙ ЦИКЛ</span>
                <h3>{level?.short} · отчёт → проверка → доработка → утверждение</h3>
                <p>Для НИР, практики и научной стажировки применяется та же логика обмена файлами и рецензирования.</p>
              </div>
              <IconBlock name="calendar" />
            </header>
            <div className="sv121-list" style={{ marginTop: 10 }}>
              {[...reports.map((item) => ({ item, cycle: 'исследовательская работа' })), ...practices.map((item) => ({ item, cycle: 'практика / стажировка' }))].map(({ item, cycle }) => (
                <div className="sv121-row" key={item.id}>
                  <div>
                    <strong>{item.title}</strong>
                    <small>{item.status} · {cycle}</small>
                  </div>
                  <div className="sv121-actions" style={{ margin: 0 }}>
                    <button
                      type="button"
                      className="sv121-btn violet"
                      onClick={() => setReview({ itemId: item.id, mode: null, status: 'DRAFT', decision: 'MINOR', summary: '', files: [] })}
                    >
                      Проверить / рецензировать
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </article>
          <section className="sv121-panel" style={{ ...tone(accent), marginTop: 14 }}>
            <span className="sv121-eyebrow">ИССЛЕДОВАТЕЛЬСКИЕ РЕЗУЛЬТАТЫ</span>
            <h2>{researchMeta.nirLabel}, статьи, конференции и evidence</h2>
            <p>
              {level?.short}: {publicationRule} {researchMeta.semester
                ? `Отчёт ${researchMeta.nirLabel} ведётся по каждому семестру отдельно: основной отчёт + отдельные подтверждающие материалы.`
                : 'Для бакалавриата предусмотрена как минимум одна апробация / конференция и основной результат дипломной работы.'}
            </p>
            <div className="sv121-grid" style={{ marginTop: 14 }}>
              <article className="sv121-card sv121-span6" style={tone('#49ddb7')}>
                <header>
                  <div>
                    <h3>{researchMeta.nirLabel}</h3>
                    <p>Основной отчёт и evidence хранятся раздельно: статьи, сертификаты, акты, данные и материалы практики.</p>
                  </div>
                  <IconBlock name="research" />
                </header>
                <div className="sv121-list" style={{ marginTop: 10 }}>
                  {reports.map((item) => (
                    <div className="sv121-row" key={item.id}>
                      <div>
                        <strong>{item.title}</strong>
                        <small>
                          {item.file_url ? `Отчёт: ${item.file_url}` : 'Отчёт не загружен'}
                          {item.evidence_url ? ` · evidence: 1 · ${item.evidence_url}` : ' · evidence: 0'}
                        </small>
                      </div>
                      <div className="sv121-actions" style={{ margin: 0 }}>
                        <button type="button" className="sv121-btn" onClick={() => void onDecide('nir', item.id, 'APPROVED', 'Отчёт утверждён')}>Проверить / утвердить</button>
                      </div>
                    </div>
                  ))}
                </div>
              </article>
              <article className="sv121-card sv121-span6" style={tone('#e8b33b')}>
                <header>
                  <div>
                    <h3>Публикации / конференции</h3>
                    <p>Результаты по теме: журнал или конференция, статус, venue и подтверждающий файл.</p>
                  </div>
                  <IconBlock name="research" />
                </header>
                <div className="sv121-list" style={{ marginTop: 10 }}>
                  {publications.length ? publications.map((item) => {
                    const [pubType, venue] = (item.note || 'Конференция · —').split(' · ');
                    return (
                      <div className="sv121-row" key={item.id}>
                        <div>
                          <strong>{item.title}</strong>
                          <small>{pubType || 'Конференция'} · {venue || '—'} · {item.status}{item.file_url ? ` · файл: ${item.file_url}` : ''}</small>
                        </div>
                        <button type="button" className="sv121-btn" onClick={() => onAttachFile(item.id, item.title)}>
                          {item.file_url ? 'Заменить / добавить файл' : '+ Файл'}
                        </button>
                      </div>
                    );
                  }) : <div className="sv121-empty">Публикации ещё не добавлены</div>}
                </div>
                <div className="sv121-actions">
                  <button type="button" className="sv121-btn gold" onClick={() => onCompose('publications')}>+ Добавить публикацию / конференцию</button>
                </div>
              </article>
              <article className="sv121-card sv121-span12" style={tone('#66b7e8')}>
                <header>
                  <div>
                    <h3>Литература, библиография и материалы по разделам</h3>
                    <p>Источник или файл связывается с конкретным разделом утверждённого плана и остаётся видимым обеим сторонам.</p>
                  </div>
                  <IconBlock name="files" />
                </header>
                <div className="sv121-list" style={{ marginTop: 10 }}>
                  {detail.literature.length ? detail.literature.map((item) => (
                    <div className="sv121-row" key={item.id}>
                      <div><strong>{item.title}</strong><small>Раздел — · {item.note || item.added_by || 'Без комментария'}</small></div>
                      <span className="sv121-badge info">Материал</span>
                    </div>
                  )) : <div className="sv121-empty">Материалы по разделам ещё не добавлены</div>}
                </div>
                <div className="sv121-actions">
                  <button type="button" className="sv121-btn" onClick={() => onCompose('literature')}>+ Добавить источник / файл</button>
                  <button type="button" className="sv121-btn" onClick={() => onOpen('literature')}>Открыть совместную литературу</button>
                </div>
              </article>
            </div>
          </section>
          {review ? (
            <div className="sv121-dialog open" role="dialog" aria-modal="true">
              <div className="sv121-dialog-box">
                <header>
                  <h2>Проверка и рецензирование · НИР / практика</h2>
                  <button type="button" onClick={() => setReview(null)} aria-label="Закрыть">×</button>
                </header>
                <div className="sv121-callout" style={{ ...tone('#66b7e8'), marginTop: 12 }}>
                  <b>Контекст:</b> Материал загружен в рабочий контур<br />
                  <b>Автор комментариев в DOCX:</b> {supervisorName}
                </div>
                {review.mode === null ? (
                  <div className="sv161-review-modes">
                    <button type="button" className="sv161-review-mode" style={tone('#41d2ff')} onClick={() => setReview({ ...review, mode: 'manual', summary: '' })}>
                      <b>01</b><strong>Рецензировать самому</strong><small>Руководитель пишет комментарий вручную и при необходимости прикладывает собственный файл.</small>
                    </button>
                    <button type="button" className="sv161-review-mode" style={tone('#49ddb7')} onClick={() => setReview({ ...review, mode: 'standard', summary: 'Материал в целом соответствует поставленной задаче, однако перед окончательным принятием необходимо усилить доказательность отдельных выводов, проверить связь результатов с целью и задачами, а также доработать академическую точность формулировок и техническую согласованность ссылок.' })}>
                      <b>02</b><strong>По утверждённому стандарту</strong><small>Три блока критериев + точечные замечания к конкретным фрагментам.</small>
                    </button>
                    <button type="button" className="sv161-review-mode" style={tone('#9e80ff')} onClick={() => setReview({ ...review, mode: 'ai', summary: 'Материал в целом соответствует поставленной задаче, однако перед окончательным принятием необходимо усилить доказательность отдельных выводов, проверить связь результатов с целью и задачами, а также доработать академическую точность формулировок и техническую согласованность ссылок.' })}>
                      <b>03</b><strong>Super AI рецензирование</strong><small>Инструкция руководителя, аудио и образцы → предварительная рецензия для проверки.</small>
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="sv161-criteria">
                      {REVIEW_CRITERIA.map((item) => (
                        <article key={item.title} style={tone(item.color)}>
                          <h4>{item.title}</h4>
                          <p>{item.text}</p>
                        </article>
                      ))}
                    </div>
                    <div className="sv121-field">
                      <label>Готовая рецензия руководителя</label>
                      <textarea value={review.summary} onChange={(event) => setReview({ ...review, summary: event.target.value })} />
                    </div>
                    {review.mode !== 'manual' ? (
                      <div className="sv161-fragments">
                        {REVIEW_FRAGMENTS.map((item, index) => (
                          <div className="sv161-fragment" key={item.fragment}>
                            <b>Примечание {index + 1} · {supervisorName}</b>
                            <q>{item.fragment}</q>
                            <p>{item.note}</p>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <div className="sv161-decision">
                      {REVIEW_DECISIONS.map(([key, label]) => (
                        <button key={key} type="button" className={review.decision === key ? 'active' : ''} onClick={() => setReview({ ...review, decision: key })}>{label}</button>
                      ))}
                    </div>
                    {review.files.length ? <p>{review.files.join(', ')}</p> : null}
                    <div className="sv121-actions">
                      <button type="button" className="sv121-btn" onClick={() => pickFile((name) => setReview({ ...review, files: [...review.files, name] }))}>+ Документ / образец</button>
                      {review.status === 'DRAFT' ? (
                        <button type="button" className="sv121-btn mint" onClick={() => { setReview({ ...review, status: 'CONFIRMED' }); onNotice('Рецензия подтверждена'); }}>Подтвердить</button>
                      ) : null}
                      {review.status === 'CONFIRMED' ? (
                        <button type="button" className="sv121-btn mint" onClick={() => { setReview({ ...review, status: 'APPROVED' }); void onDecide('nir', review.itemId, 'APPROVED', 'Рецензия утверждена руководителем'); }}>Утвердить</button>
                      ) : null}
                      {review.status === 'APPROVED' ? (
                        <button type="button" className="sv121-btn gold" onClick={() => { setReview({ ...review, status: 'SENT' }); onNotice('Рецензия отправлена обучающемуся'); }}>Отправить обучающемуся</button>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : null}
        </>
      )}

      {view === 'timeline' && (
        <section className="sv121-panel" style={tone(accent)}>
          <span className="sv121-eyebrow">РЕДАКТИРУЕМАЯ ХРОНОЛОГИЯ</span>
          <h2>{level?.short} · {level?.duration}</h2>
          <p>Можно менять даты, порядок, добавлять собственные контрольные точки и отмечать фактическое завершение.</p>
          <div className="sv121-timeline" style={{ marginTop: 14 }}>
            {detail.deadlines.map((item, index) => (
              <div key={item.id} className="sv121-step" style={tone(item.status === 'DONE' || item.status === 'COMPLETED' ? '#49ddb7' : item.status === 'ACTIVE' ? '#e8b33b' : '#66b7e8')}>
                <span className="n">{String(index + 1).padStart(2, '0')}</span>
                <div><h4>{item.title}</h4><p>{item.date} · {item.status}</p></div>
                {item.status !== 'CONFIRMED' && item.status !== 'DONE' ? (
                  <button type="button" className="sv121-btn" onClick={() => void onDecide('deadlines', item.id, 'CONFIRMED')}>Управлять</button>
                ) : <span className={`sv121-badge ${badgeClass(item.status)}`}>{item.status}</span>}
              </div>
            ))}
          </div>
        </section>
      )}

      {view === 'meetings' && (
        <section className="sv121-grid sv122-consult-grid">
          <article className="sv121-card sv121-span7" style={tone('#66b7e8')}>
            <header>
              <div>
                <span className="sv121-eyebrow">ВСТРЕЧИ · КОНСУЛЬТАЦИИ · ПРИГЛАШЕНИЯ</span>
                <h3>Консультации · {student.full_name}</h3>
                <p>Каждая встреча имеет дату, повестку, вопросы, протокол и следующие действия.</p>
              </div>
              <IconBlock name="chat" />
            </header>
            <div className="sv121-actions">
              <button type="button" className="sv121-btn gold" onClick={() => onCompose('consultations')}>+ Назначить консультацию</button>
            </div>
            <div className="sv121-list" style={{ marginTop: 12 }}>
              {detail.consultations.map((item) => (
                <div className="sv121-row" key={item.id}>
                  <div><strong>{item.title}</strong><small>{item.date} · {item.status}</small></div>
                  <div className="sv121-actions" style={{ margin: 0 }}>
                    {item.status !== 'COMPLETED' ? (
                      <button type="button" className="sv121-btn" onClick={() => void onDecide('consultations', item.id, 'COMPLETED')}>Протокол</button>
                    ) : <span className="sv121-badge ok">{item.status}</span>}
                  </div>
                </div>
              ))}
            </div>
          </article>
          <article className="sv121-card sv121-span5" style={tone('#9e80ff')}>
            <header>
              <div>
                <span className="sv121-eyebrow">ОБЩАЯ ПЕРЕПИСКА</span>
                <h3>{student.full_name} ↔ руководитель</h3>
                <p>Вопросы по заданиям остаются внутри задач, общие вопросы и договорённости — здесь.</p>
              </div>
              <IconBlock name="chat" />
            </header>
            <div className="sv121-actions">
              <button type="button" className="sv121-btn" onClick={() => onCompose('messages')}>+ Сообщение</button>
            </div>
            <div className="sv121-list" style={{ marginTop: 12, maxHeight: 420, overflow: 'auto' }}>
              {(detail.messages || []).length ? (detail.messages || []).map((item) => (
                <div className="sv121-row" key={item.id}>
                  <div><strong>{item.text}</strong><small>{item.author || 'Руководитель'} · {item.at || ''}</small></div>
                </div>
              )) : <div className="sv121-empty">Сообщений пока нет</div>}
            </div>
          </article>
        </section>
      )}

      {view === 'defense' && (
        <>
          {student.education_level === 'DOCTORAL' ? (
            <section className="sv121-panel" style={tone('#49ddb7')}>
              <div className="sv121-callout" style={tone('#e8b33b')}>
                <b>Важно:</b> маршрут предзащиты и защиты сверяется с требованиями года фактической защиты и учёным секретарём.
              </div>
              <span className="sv121-eyebrow" style={{ display: 'block', marginTop: 14 }}>PhD · ЭТАПЫ ЗАЩИТЫ</span>
              <h2>От предварительной защиты до архива</h2>
              <div className="sv121-list" style={{ marginTop: 14 }}>
                {detail.defense.stages.map((item, index) => (
                  <article key={item.id} className="sv121-card" style={tone(['#e8b33b', '#9e80ff', '#41d2ff', '#49ddb7', '#ff7663'][index % 5])}>
                    <header>
                      <div>
                        <span className="sv121-eyebrow">ЭТАП {index + 1} · {item.status}</span>
                        <h3>{item.title}</h3>
                        <p>{detail.defense.status}</p>
                      </div>
                      <IconBlock name={index === 1 ? 'shield' : 'files'} />
                    </header>
                    <div className="sv121-progress" style={{ ['--p' as string]: item.status === 'READY' ? '100%' : '35%' }}><span /></div>
                    <div className="sv121-actions">
                      {item.status !== 'READY' ? (
                        <button type="button" className="sv121-btn" onClick={() => void onDecide('defense', item.id, 'READY')}>Открыть документы и чек-лист →</button>
                      ) : <span className="sv121-badge ok">Готово</span>}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ) : (
            <section className="sv121-panel" style={tone(accent)}>
              <span className="sv121-eyebrow">ФИНАЛЬНЫЙ МАРШРУТ</span>
              <h2>{level?.short} · подготовка к защите</h2>
              <p>Сроки и порядок редактируются руководителем. Для каждого этапа можно хранить результат, комментарий и подтверждающий документ.</p>
              <div className="sv121-timeline" style={{ marginTop: 14 }}>
                {detail.defense.stages.map((item, index) => (
                  <div key={item.id} className="sv121-step" style={tone(index === 0 ? '#e8b33b' : '#66b7e8')}>
                    <span className="n">{String(index + 1).padStart(2, '0')}</span>
                    <div><h4>{item.title}</h4><p>{item.status}</p></div>
                    {item.status !== 'READY' ? (
                      <button type="button" className="sv121-btn" onClick={() => void onDecide('defense', item.id, 'READY')}>Управлять</button>
                    ) : <span className="sv121-badge ok">Готово</span>}
                  </div>
                ))}
              </div>
            </section>
          )}
          <section className="sv121-panel" style={{ ...tone('#e8b33b'), marginTop: 14 }}>
            <span className="sv121-eyebrow">СКВОЗНАЯ ФИНАЛЬНАЯ ТРАЕКТОРИЯ</span>
            <h2>От полной работы до основной защиты</h2>
            <p>Этапы дополняют ранее утверждённый маршрут и не заменяют существующие требования конкретного уровня.</p>
            <div className="sv121-timeline" style={{ marginTop: 12 }}>
              {detail.defense.stages.map((item, index) => (
                <div key={`final-${item.id}`} className="sv121-step" style={tone('#e8b33b')}>
                  <span className="n">{String(index + 1).padStart(2, '0')}</span>
                  <div><h4>{item.title}</h4><p>{item.status}</p></div>
                  <button type="button" className="sv121-btn" onClick={() => void onSave({ defence_status: 'PRE_DEFENSE' })}>Управлять</button>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      {view === 'ai' && (
        <section className="sv121-grid">
          <article className="sv121-card sv121-span8 sv121-ai" style={tone('#9e80ff')}>
            <header>
              <div>
                <span className="sv121-eyebrow">ИИ-помощник · предварительный режим</span>
                <h3>AI-помощник научного руководителя</h3>
                <p>AI не утверждает научные решения автоматически. Он готовит проект, который должен проверить человек.</p>
              </div>
              <IconBlock name="spark" />
            </header>
            <div className="sv121-actions">
              <button type="button" className="sv121-btn violet" onClick={() => void onAsk('Темы исследования')}>Темы исследования</button>
              <button type="button" className="sv121-btn violet" onClick={() => void onAsk('Вопросы по теме')}>Вопросы по теме</button>
              <button type="button" className="sv121-btn violet" onClick={() => void onAsk('Проверить структуру')}>Проверить структуру</button>
              <button type="button" className="sv121-btn violet" onClick={() => void onAsk('Разбить на задачи')}>Разбить на задачи</button>
              <button type="button" className="sv121-btn violet" onClick={() => void onAsk('Риски сроков')}>Риски сроков</button>
              <button type="button" className="sv121-btn violet" onClick={() => void onAsk('Черновик комментария')}>Черновик комментария</button>
            </div>
            <div className="sv121-ai-output">{detail.last_ai?.answer || 'Выберите действие.'}</div>
            {detail.last_ai?.suggestions?.length ? (
              <div className="sv121-list" style={{ marginTop: 12 }}>
                {detail.last_ai.suggestions.map((item) => (
                  <div className="sv121-row" key={item}><div><strong>{item}</strong></div></div>
                ))}
              </div>
            ) : null}
          </article>
          <article className="sv121-card sv121-span4" style={tone('#49ddb7')}>
            <span className="sv121-eyebrow">ПРАВИЛО</span>
            <h3>Human approval first</h3>
            <p>Любая тема, оценка, комментарий, изменение плана или решение по защите остаётся под контролем научного руководителя.</p>
          </article>
        </section>
      )}

      {view === 'literature' && (
        <section className="sv121-panel" style={tone('#66b7e8')}>
          <span className="sv121-eyebrow">СОВМЕСТНАЯ ЛИТЕРАТУРА · {level?.short}</span>
          <h2>Библиотека научного руководителя и обучающегося</h2>
          <p>Закреплённый вами источник сразу доступен обучающемуся. Источник обучающегося сначала появляется у вас на согласование.</p>
          <div className="sv121-actions">
            <button type="button" className="sv121-btn gold" onClick={() => onCompose('literature')}>+ Добавить источник / книгу / файл</button>
          </div>
          <div className="sv121-list" style={{ marginTop: 14 }}>
            {detail.literature.map((item) => {
              const fromSupervisor = /supervisor|руковод/i.test(item.added_by || '');
              return (
                <article key={item.id} className="sv121-card" style={tone(fromSupervisor ? '#49ddb7' : '#66b7e8')}>
                  <header>
                    <div>
                      <span className="sv121-eyebrow">{fromSupervisor ? 'ОТ РУКОВОДИТЕЛЯ' : 'ОТ ОБУЧАЮЩЕГОСЯ'}</span>
                      <h3>{item.title}</h3>
                      <p>{item.note || item.added_by || 'Без комментария'}</p>
                    </div>
                    <IconBlock name="files" />
                  </header>
                  <div className="sv121-actions">
                    <span className={`sv121-badge ${badgeClass(item.status)}`}>{item.status === 'APPROVED' ? 'Можно использовать' : item.status === 'REJECTED' ? 'Не использовать' : 'Ожидает решения'}</span>
                    {item.status !== 'APPROVED' ? (
                      <button type="button" className="sv121-btn mint" onClick={() => void onDecide('literature', item.id, 'APPROVED')}>Можно использовать</button>
                    ) : null}
                    {item.status !== 'REJECTED' ? (
                      <button type="button" className="sv121-btn danger" onClick={() => void onDecide('literature', item.id, 'REJECTED')}>Не использовать</button>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {view === 'writing' && (
        <section className="sv121-panel" style={tone('#49ddb7')}>
          <span className="sv121-eyebrow">ОСНОВНОЙ ЭТАП НАПИСАНИЯ · {level?.label}</span>
          <h2>Структура работы синхронизируется с утверждённым планом</h2>
          <p>Титульные листы, задание, содержание, введение, разделы утверждённого плана, заключение, литература и приложения превращаются в рабочие блоки обучающегося.</p>
          <div className="sv161-flow">
            <div style={tone('#41d2ff')}><small>Структура</small><strong>{detail.thesis.length || detail.plan.sections.length} блоков</strong></div>
            <div style={tone('#9e80ff')}><small>Принято</small><strong>{detail.thesis.filter((item) => item.status === 'APPROVED').length}/{detail.thesis.length || detail.plan.sections.length}</strong></div>
            <div style={tone('#e8b33b')}><small>Версия плана</small><strong>{detail.plan.version}</strong></div>
            <div style={tone('#49ddb7')}><small>Статус</small><strong>{detail.plan.status}</strong></div>
          </div>
          <div className="sv121-actions">
            <button type="button" className="sv121-btn mint" onClick={() => void onPublishDraft(draftPayload())}>Опубликовать изменения обучающемуся</button>
          </div>
          <div className="sv161-work-tree">
            {(detail.thesis.length
              ? detail.thesis.map((item) => ({ key: item.id, title: item.title, status: item.status, meta: item.version, decidable: true }))
              : detail.plan.sections.map((item) => ({ key: item.num || item.title, title: `${item.num ? `${item.num}. ` : ''}${item.title}`, status: item.status, meta: item.status, decidable: false }))
            ).map((item) => (
              <article key={item.key} className="sv161-work-node" style={tone(item.status === 'APPROVED' || item.status === 'COMPLETED' ? '#49ddb7' : item.status === 'RETURNED' ? '#ff7663' : '#66b7e8')}>
                <header>
                  <div>
                    <span className="sv121-eyebrow">ОБЯЗАТЕЛЬНЫЙ БЛОК</span>
                    <h4>{item.title}</h4>
                    <div className="sv161-statusline">
                      <span className={`sv121-badge ${badgeClass(item.status)}`}>{item.status}</span>
                      <span className="sv121-badge info">{item.meta}</span>
                    </div>
                  </div>
                </header>
                {item.decidable && item.status !== 'APPROVED' ? (
                  <div className="sv121-actions">
                    <button type="button" className="sv121-btn mint" onClick={() => void onDecide('thesis', item.key, 'APPROVED')}>Принять блок</button>
                  </div>
                ) : null}
              </article>
            ))}
          </div>
          <article className="sv121-card" style={{ ...tone('#e8b33b'), marginTop: 14 }}>
            <header>
              <div>
                <span className="sv121-eyebrow">ИТОГОВАЯ СБОРКА И ПРЕДВАРИТЕЛЬНАЯ ПРОВЕРКА</span>
                <h3>Собрать общий материал</h3>
                <p>После принятия рабочих блоков руководитель собирает единую версию, проверяет содержание, техническое оформление, литературу, визуальные материалы и комплектность.</p>
              </div>
              <IconBlock name="shield" />
            </header>
            <div className="sv161-checks">
              {CHECKS.map((label) => (
                <label key={label}>
                  <input
                    type="checkbox"
                    checked={Boolean(draft.checks[label])}
                    onChange={(event) => setDraft((current) => ({
                      ...current,
                      checks: { ...current.checks, [label]: event.target.checked },
                    }))}
                  /> {label}
                </label>
              ))}
            </div>
          </article>
        </section>
      )}
    </>
  );
}
